import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Tooltip,
    LinearProgress,
    Tab,
    Tabs,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    PlayArrow as PlayIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Loop as LoopIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    BatteryChargingFull as BatteryIcon,
    DirectionsCar as EvIcon,
    AcUnit as ColdIcon,
    TrendingDown as SavingsIcon,
    Science as ScienceIcon,
    LocationOn as LocationIcon,
    ElectricBolt as BoltIcon,
} from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer,
} from 'recharts';
import { useAuth, useThemeMode } from '@vortx/shared-ui';
import { useSimulation } from '../hooks/useSimulation';
import { getFacilitySummary, getSimulationScenarios } from '../services/simulationService';
import LiveProgressView from '../components/simulation/LiveProgressView';
import DailyPlaybackTab from '../components/simulation/DailyPlaybackTab';

// --- Vortx brand colors ---
const GOLD = '#b28c50';
const GOLD_LIGHT = '#d4a574';
const GOLD_GRADIENT = 'linear-gradient(135deg, #b28c50 0%, #d4a574 100%)';
const HEADING_FONT = "'Syne', sans-serif";
const MONO_FONT = "'JetBrains Mono', monospace";
const SUCCESS = '#2e7d32';
const ERROR_COLOR = '#d32f2f';

function useThemeColors() {
    const { isRetro } = useThemeMode();
    return {
        isRetro,
        BG: isRetro ? '#1a1a1a' : 'transparent',
        CARD_BG: isRetro ? '#242424' : '#fff',
        TEXT_PRIMARY: isRetro ? '#f4f1ec' : '#2c2c2c',
        TEXT_SECONDARY: isRetro ? '#888' : '#666',
        GOLD_BORDER: isRetro ? 'rgba(178, 140, 80, 0.2)' : 'rgba(0, 0, 0, 0.12)',
        GOLD_HOVER: isRetro ? 'rgba(178, 140, 80, 0.15)' : 'rgba(178, 140, 80, 0.08)',
    };
}

export default function SimulationPage({ projectId: propProjectId }) {
    const { projectId: paramProjectId } = useParams();
    const projectId = propProjectId || paramProjectId;
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { isRetro, BG, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER, GOLD_HOVER } = useThemeColors();

    const {
        runs, activeRun, loading, error,
        startRun, cancelRun, deleteRun, loadRun, setActiveRun,
    } = useSimulation(projectId);

    // Facility summary from backend
    const [facility, setFacility] = useState(null);
    const [facilityLoading, setFacilityLoading] = useState(true);

    // Scenario selection
    const [scenarios, setScenarios] = useState({ site_dna: [], ev: [], cold: [] });
    const [selectedSiteDna, setSelectedSiteDna] = useState('');
    const [selectedEv, setSelectedEv] = useState('');
    const [selectedCold, setSelectedCold] = useState('');

    // Run state
    const [starting, setStarting] = useState(false);
    const [startError, setStartError] = useState(null);

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Tab state for results view
    const [activeTab, setActiveTab] = useState(0);

    // Load facility summary
    const loadFacility = useCallback(async () => {
        if (!currentUser || !projectId) return;
        try {
            setFacilityLoading(true);
            const data = await getFacilitySummary(projectId, currentUser);
            setFacility(data);
        } catch (err) {
            console.error('Failed to load facility summary:', err);
        } finally {
            setFacilityLoading(false);
        }
    }, [currentUser, projectId]);

    useEffect(() => {
        loadFacility();
    }, [loadFacility]);

    // Load available scenarios
    useEffect(() => {
        if (!currentUser || !projectId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await getSimulationScenarios(projectId, currentUser);
                if (cancelled) return;
                setScenarios(data);
                // Auto-select first (latest) scenario for each
                if (data.site_dna?.length > 0) {
                    setSelectedSiteDna(data.site_dna[0].id);
                }
                if (data.ev?.length > 0) {
                    setSelectedEv(data.ev[0].id);
                }
                if (data.cold?.length > 0) {
                    setSelectedCold(data.cold[0].id);
                }
            } catch (err) {
                console.error('Failed to load scenarios:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser, projectId]);

    // --- Handlers ---

    const handleStartSimulation = async () => {
        setStarting(true);
        setStartError(null);
        try {
            const overrides = {};
            if (selectedSiteDna) overrides.site_dna_scenario_id = selectedSiteDna;
            if (selectedEv) overrides.ev_scenario_id = selectedEv;
            if (selectedCold) overrides.cold_scenario_id = selectedCold;
            await startRun(Object.keys(overrides).length > 0 ? overrides : undefined);
        } catch (err) {
            setStartError(err.message);
        } finally {
            setStarting(false);
        }
    };

    const handleDeleteRun = async () => {
        if (!deleteTarget) return;
        await deleteRun(deleteTarget);
        setDeleteTarget(null);
    };

    const handleViewRun = (run) => {
        loadRun(run.id);
    };

    const isRunning = activeRun && activeRun.status === 'running';
    const isComplete = activeRun && activeRun.status === 'completed';
    const isFailed = activeRun && activeRun.status === 'failed';
    const isCancelled = activeRun && activeRun.status === 'cancelled';
    const hasAgents = facility?.has_agents;
    const agents = facility?.agents || {};

    // Normalize agent keys: backend returns 'site_dna' when PV+battery uses LP agent
    const batteryAgent = agents.battery || agents.site_dna;

    const results = activeRun?.results || {};
    const siloed = results.siloed || {};
    const orchestrated = results.orchestrated || {};
    const comparison = results.comparison || {};
    const savings = comparison.savings || {};

    return (
        <Box sx={{ minHeight: '100vh', background: BG, p: { xs: 2, md: 4 } }}>
            {/* Back button */}
            <Button
                startIcon={<BackIcon />}
                onClick={() => navigate('/all-projects')}
                sx={{ color: TEXT_SECONDARY, mb: 2, '&:hover': { color: GOLD, background: 'rgba(178,140,80,0.1)' } }}
            >
                Back to Projects
            </Button>

            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScienceIcon sx={{ fontSize: '2rem', color: GOLD }} />
                    Annual Simulation
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mt: 0.5 }}>
                    Compare siloed vs orchestrated multi-agent operation over a full year
                </Typography>
            </Box>

            {/* Site Info Strip */}
            {facility && (
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    mb: 3,
                    p: 2,
                    bgcolor: CARD_BG,
                    border: `1px solid ${GOLD_BORDER}`,
                    borderRadius: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}>
                    <InfoItem
                        icon={<LocationIcon sx={{ color: GOLD, fontSize: 18 }} />}
                        label="Location"
                        value={[
                            facility.location?.city,
                            facility.location?.state,
                        ].filter(Boolean).join(', ') || `${facility.location?.lat?.toFixed(4)}, ${facility.location?.lon?.toFixed(4)}`}
                        secondary={facility.location?.zip_code}
                        colors={{ TEXT_PRIMARY, TEXT_SECONDARY, isRetro }}
                    />
                    <Box sx={{ width: '1px', height: 32, bgcolor: GOLD_BORDER, flexShrink: 0 }} />
                    <InfoItem
                        icon={<BoltIcon sx={{ color: GOLD, fontSize: 18 }} />}
                        label="Tariff"
                        value={facility.tariff?.name || 'Not configured'}
                        secondary={facility.tariff?.utility}
                        colors={{ TEXT_PRIMARY, TEXT_SECONDARY, isRetro }}
                    />
                    <Box sx={{ width: '1px', height: 32, bgcolor: GOLD_BORDER, flexShrink: 0 }} />
                    <InfoItem
                        icon={<BoltIcon sx={{ color: GOLD, fontSize: 18 }} />}
                        label="Service"
                        value={facility.site_capacity_amps
                            ? `${facility.site_capacity_amps}A · ${facility.service_voltage || '—'}V`
                            : `${Math.round(facility.site_capacity_kw)} kW`}
                        secondary={facility.site_capacity_amps && facility.site_capacity_kw
                            ? `${Math.round(facility.site_capacity_kw)} kW`
                            : null}
                        colors={{ TEXT_PRIMARY, TEXT_SECONDARY, isRetro }}
                    />
                </Box>
            )}

            {/* Error banner */}
            {(error || startError) && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStartError(null)}>
                    {startError || error}
                </Alert>
            )}

            {/* Section 1: Facility Summary Cards */}
            {facilityLoading ? (
                <Box textAlign="center" py={4}>
                    <CircularProgress sx={{ color: GOLD }} size={32} />
                </Box>
            ) : (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={4}>
                        <AgentCard
                            icon={<BatteryIcon />}
                            label={agents.site_dna ? 'Solar + Battery' : 'Battery'}
                            source="Site DNA"
                            configured={!!batteryAgent}
                            details={batteryAgent ? [
                                `${batteryAgent.capacity_kwh} kWh`,
                                `${batteryAgent.max_power_kw} kW`,
                                ...(batteryAgent.pv_capacity_kw ? [`${batteryAgent.pv_capacity_kw} kW PV`] : []),
                            ] : null}
                            scenarios={scenarios.site_dna}
                            selectedScenario={selectedSiteDna}
                            onScenarioChange={setSelectedSiteDna}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AgentCard
                            icon={<EvIcon />}
                            label="EV Fleet"
                            source="Flux EV"
                            configured={!!agents.ev_fleet}
                            details={agents.ev_fleet ? [
                                `${agents.ev_fleet.vehicle_count} vehicles`,
                                `${agents.ev_fleet.total_battery_kwh} kWh fleet`,
                                ...(agents.ev_fleet.total_charger_kw ? [`${agents.ev_fleet.total_charger_kw} kW chargers`] : []),
                            ] : null}
                            scenarios={scenarios.ev}
                            selectedScenario={selectedEv}
                            onScenarioChange={setSelectedEv}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AgentCard
                            icon={<ColdIcon />}
                            label="Cold Storage"
                            source="Flux COLD"
                            configured={!!agents.cold_storage}
                            details={agents.cold_storage ? [
                                `${agents.cold_storage.zone_count} zones`,
                                ...(agents.cold_storage.total_compressor_kw ? [`${agents.cold_storage.total_compressor_kw} kW peak HVAC`] : []),
                            ] : null}
                            scenarios={scenarios.cold}
                            selectedScenario={selectedCold}
                            onScenarioChange={setSelectedCold}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Section 2: Simulation Controls */}
            <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={starting ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <PlayIcon />}
                        onClick={handleStartSimulation}
                        disabled={starting || isRunning || !hasAgents}
                        sx={{
                            background: GOLD_GRADIENT, color: '#000',
                            fontFamily: HEADING_FONT, fontWeight: 600, px: 4, py: 1.2,
                            '&:hover': { background: 'linear-gradient(135deg, #c9a060 0%, #e0b888 100%)' },
                            '&.Mui-disabled': { background: '#555', color: '#999' },
                        }}
                    >
                        {starting ? 'Starting...' : isRunning ? 'Running...' : 'Run Annual Simulation'}
                    </Button>
                    {facility?.site_capacity_kw && (
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT }}>
                            Site capacity: {facility.site_capacity_kw} kW
                        </Typography>
                    )}
                </Box>
                {!hasAgents && !facilityLoading && (
                    <Typography variant="caption" sx={{ color: ERROR_COLOR, mt: 1, display: 'block' }}>
                        No agents configured. Add battery, EV fleet, or cold storage to the project first.
                    </Typography>
                )}
            </Paper>

            {/* Section 3: Progress (while running) */}
            {isRunning && (
                <LiveProgressView
                    run={activeRun}
                    agents={agents}
                    themeColors={{ isRetro, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER }}
                    onCancel={() => cancelRun(activeRun.id)}
                />
            )}

            {/* Section 4: Results (when complete) */}
            {isComplete && (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{
                            mb: 3,
                            '& .MuiTab-root': { color: TEXT_SECONDARY, fontFamily: HEADING_FONT, textTransform: 'none' },
                            '& .Mui-selected': { color: GOLD },
                            '& .MuiTabs-indicator': { backgroundColor: GOLD },
                        }}
                    >
                        <Tab label="Overview" />
                        <Tab label="Daily Playback" />
                        <Tab label="History" />
                    </Tabs>

                    {activeTab === 0 && (
                        <>
                            <SavingsBanner savings={savings} />
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={6}>
                                    <ComparisonCard title="Siloed Operation" subtitle="Each agent optimizes independently" data={siloed} color={TEXT_SECONDARY} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ComparisonCard title="Orchestrated" subtitle="Vortx coordinates all agents" data={orchestrated} color={GOLD} highlight />
                                </Grid>
                            </Grid>
                            <MonthlyChart comparison={comparison} />
                            {(siloed.agents || orchestrated.agents) && (
                                <AgentBreakdown siloed={siloed} orchestrated={orchestrated} />
                            )}
                        </>
                    )}

                    {activeTab === 1 && (
                        <DailyPlaybackTab
                            dailyProfiles={results.daily_profiles || []}
                            siteCapacityKw={facility?.site_capacity_kw}
                            agents={agents}
                            themeColors={{ isRetro, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER }}
                        />
                    )}

                    {activeTab === 2 && runs.length > 0 && (
                        <RunHistoryTable
                            runs={runs}
                            activeRunId={activeRun?.id}
                            onView={handleViewRun}
                            onDelete={(runId) => setDeleteTarget(runId)}
                        />
                    )}
                </>
            )}

            {/* Failed run display */}
            {isFailed && (
                <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${ERROR_COLOR}33` }}>
                    <Typography variant="h6" sx={{ color: ERROR_COLOR, fontFamily: HEADING_FONT, mb: 1 }}>
                        Simulation Failed
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                        {activeRun.results?.error || 'An unknown error occurred.'}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setActiveRun(null)}
                        sx={{ mt: 2, color: TEXT_SECONDARY, borderColor: GOLD_BORDER }}
                    >
                        Dismiss
                    </Button>
                </Paper>
            )}

            {/* Cancelled run display */}
            {isCancelled && (
                <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                    <Typography variant="h6" sx={{ color: TEXT_SECONDARY, fontFamily: HEADING_FONT, mb: 1 }}>
                        Simulation Cancelled
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                        The simulation was stopped before completing.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setActiveRun(null)}
                        sx={{ mt: 2, color: TEXT_SECONDARY, borderColor: GOLD_BORDER }}
                    >
                        Dismiss
                    </Button>
                </Paper>
            )}

            {/* Section 5: Run History (when no run is actively viewed) */}
            {!isComplete && !isRunning && !isFailed && !isCancelled && !loading && runs.length > 0 && (
                <RunHistoryTable
                    runs={runs}
                    activeRunId={activeRun?.id}
                    onView={handleViewRun}
                    onDelete={(runId) => setDeleteTarget(runId)}
                />
            )}

            {/* Empty state */}
            {!loading && !error && runs.length === 0 && !activeRun && !facilityLoading && (
                <Paper sx={{ p: 8, textAlign: 'center', background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
                    <ScienceIcon sx={{ fontSize: '4rem', color: TEXT_SECONDARY, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: TEXT_PRIMARY, mb: 1 }}>No Simulation Runs Yet</Typography>
                    <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 3 }}>
                        Run your first annual simulation to compare siloed vs orchestrated operation.
                    </Typography>
                </Paper>
            )}

            {/* Loading spinner */}
            {loading && !activeRun && (
                <Box textAlign="center" py={8}>
                    <CircularProgress sx={{ color: GOLD }} />
                    <Typography sx={{ color: GOLD, mt: 2, fontFamily: MONO_FONT, fontSize: '0.85rem' }}>LOADING RUNS...</Typography>
                </Box>
            )}

            {/* Delete Confirmation */}
            <Dialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                PaperProps={{ sx: { bgcolor: isRetro ? '#242424' : '#fff', border: `1px solid ${GOLD_BORDER}`, borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontFamily: HEADING_FONT, color: '#f4f1ec' }}>Delete Simulation Run?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#a6a19b' }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ color: '#a6a19b' }}>Cancel</Button>
                    <Button onClick={handleDeleteRun} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ============================================================
// Sub-components
// ============================================================

function InfoItem({ icon, label, value, secondary, colors }) {
    const { TEXT_PRIMARY, TEXT_SECONDARY, isRetro } = colors;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            {icon}
            <Box>
                <Typography sx={{
                    color: TEXT_SECONDARY,
                    fontFamily: isRetro ? MONO_FONT : 'inherit',
                    textTransform: 'uppercase',
                    fontSize: '0.6rem',
                    letterSpacing: '0.08em',
                }}>
                    {label}
                </Typography>
                <Typography sx={{ color: TEXT_PRIMARY, fontSize: '0.85rem', lineHeight: 1.2 }}>
                    {value}
                    {secondary && (
                        <span style={{ color: TEXT_SECONDARY, marginLeft: 8, fontSize: '0.8rem' }}>{secondary}</span>
                    )}
                </Typography>
            </Box>
        </Box>
    );
}

function AgentCard({ icon, label, source, configured, details, scenarios, selectedScenario, onScenarioChange }) {
    const { isRetro, CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER, GOLD_HOVER } = useThemeColors();
    return (
        <Paper sx={{
            p: 2.5, background: CARD_BG,
            border: `1px solid ${configured ? GOLD_BORDER : 'rgba(255,255,255,0.06)'}`,
            opacity: configured ? 1 : 0.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: configured ? GOLD : TEXT_SECONDARY }}>{icon}</Box>
                <Typography variant="subtitle2" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT }}>
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ color: TEXT_SECONDARY, ml: 'auto' }}>
                    {source}
                </Typography>
            </Box>
            {configured && details ? (
                details.map((d, i) => (
                    <Typography key={i} variant="body2" sx={{ color: GOLD, fontFamily: MONO_FONT, fontSize: '0.85rem' }}>
                        {d}
                    </Typography>
                ))
            ) : (
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontStyle: 'italic' }}>
                    Not Configured
                </Typography>
            )}
            {scenarios && scenarios.length > 0 && (
                <FormControl size="small" fullWidth sx={{ mt: 1.5 }}>
                    <Select
                        value={selectedScenario || ''}
                        onChange={(e) => onScenarioChange(e.target.value)}
                        displayEmpty
                        sx={{
                            color: TEXT_PRIMARY,
                            fontFamily: MONO_FONT,
                            fontSize: '0.8rem',
                            height: 36,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: GOLD_BORDER },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                            '& .MuiSelect-icon': { color: TEXT_SECONDARY },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    bgcolor: isRetro ? '#2a2a2a' : '#fff',
                                    border: `1px solid ${GOLD_BORDER}`,
                                    '& .MuiMenuItem-root': {
                                        color: TEXT_PRIMARY,
                                        fontFamily: MONO_FONT,
                                        fontSize: '0.8rem',
                                        '&:hover': { backgroundColor: GOLD_HOVER },
                                        '&.Mui-selected': { backgroundColor: 'rgba(178,140,80,0.15)' },
                                    },
                                },
                            },
                        }}
                    >
                        {scenarios.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.name}
                                {s.updated_at && (
                                    <Typography component="span" sx={{ color: TEXT_SECONDARY, ml: 1, fontSize: '0.7rem' }}>
                                        {new Date(s.updated_at).toLocaleDateString()}
                                    </Typography>
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Paper>
    );
}

function ProgressPanel({ run }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    const progress = run.progress || {};
    const phase = progress.phase || 'initializing';
    const message = progress.message || 'Starting simulation...';

    const phaseLabels = {
        initializing: 'Initializing',
        weather: 'Fetching Weather',
        tariff: 'Loading Tariff',
        siloed: 'Running Siloed Simulation',
        orchestrated: 'Running Orchestrated Simulation',
    };

    return (
        <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT }}>
                    Simulation Running
                </Typography>
                <Chip
                    label={phaseLabels[phase] || phase}
                    size="small"
                    icon={<LoopIcon />}
                    sx={{ color: GOLD, borderColor: GOLD, fontFamily: MONO_FONT }}
                    variant="outlined"
                />
            </Box>
            <LinearProgress
                variant="indeterminate"
                sx={{
                    height: 6, borderRadius: 3, mb: 1.5,
                    backgroundColor: 'rgba(178,140,80,0.15)',
                    '& .MuiLinearProgress-bar': { background: GOLD_GRADIENT, borderRadius: 3 },
                }}
            />
            <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                {message}
            </Typography>
        </Paper>
    );
}

function SavingsBanner({ savings }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    const totalSavings = savings.total_cost || 0;
    const pctSavings = savings.pct_savings || 0;
    const isPositive = totalSavings > 0;

    return (
        <Paper sx={{
            p: 3, mb: 4, textAlign: 'center',
            background: isPositive
                ? 'linear-gradient(135deg, rgba(178,140,80,0.15) 0%, rgba(178,140,80,0.05) 100%)'
                : CARD_BG,
            border: `1px solid ${isPositive ? GOLD : GOLD_BORDER}`,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                <SavingsIcon sx={{ color: isPositive ? GOLD : TEXT_SECONDARY }} />
                <Typography variant="h5" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, fontWeight: 700 }}>
                    {isPositive ? 'Vortx Orchestration Saves' : 'Orchestration Comparison'}
                </Typography>
            </Box>
            <Typography variant="h3" sx={{ color: GOLD, fontFamily: MONO_FONT, fontWeight: 700 }}>
                ${Math.abs(totalSavings).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                <Typography component="span" variant="h5" sx={{ color: GOLD_LIGHT, ml: 1 }}>
                    ({pctSavings.toFixed(1)}%)
                </Typography>
            </Typography>
            {savings.violations_avoided > 0 && (
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mt: 1 }}>
                    {savings.violations_avoided.toLocaleString()} constraint violations eliminated
                </Typography>
            )}
        </Paper>
    );
}

function ComparisonCard({ title, subtitle, data, color, highlight }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    return (
        <Paper sx={{
            p: 3, background: CARD_BG,
            border: `1px solid ${highlight ? GOLD : GOLD_BORDER}`,
        }}>
            <Typography variant="h6" sx={{ color: highlight ? GOLD : TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" sx={{ color: TEXT_SECONDARY, display: 'block', mb: 2 }}>
                {subtitle}
            </Typography>

            <MetricRow label="Annual Total" value={fmtCost(data.annual_total_cost)} color={color} large />
            <MetricRow label="Energy Cost" value={fmtCost(data.annual_energy_cost)} />
            <MetricRow label="Demand Cost" value={fmtCost(data.annual_demand_cost)} />
            <MetricRow label="Peak Demand" value={data.annual_peak_kw != null ? `${data.annual_peak_kw.toFixed(1)} kW` : 'N/A'} />
            <MetricRow
                label="Violations"
                value={data.constraint_violations != null ? data.constraint_violations.toLocaleString() : 'N/A'}
                valueColor={data.constraint_violations === 0 ? SUCCESS : ERROR_COLOR}
            />
        </Paper>
    );
}

function MetricRow({ label, value, color, valueColor, large }) {
    const { TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, borderBottom: `1px solid ${GOLD_BORDER}` }}>
            <Typography variant={large ? 'body1' : 'body2'} sx={{ color: TEXT_SECONDARY }}>
                {label}
            </Typography>
            <Typography
                variant={large ? 'h6' : 'body2'}
                sx={{
                    color: valueColor || color || TEXT_PRIMARY,
                    fontFamily: MONO_FONT,
                    fontWeight: large ? 700 : 400,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function MonthlyChart({ comparison }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    const monthly = comparison.monthly || [];
    if (monthly.length === 0) return null;

    const chartData = monthly.map((m) => ({
        month: m.month,
        Siloed: Math.round(m.siloed_cost),
        Orchestrated: Math.round(m.orchestrated_cost),
    }));

    return (
        <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
            <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 2 }}>
                Monthly Cost Comparison
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(178,140,80,0.1)" />
                    <XAxis
                        dataKey="month"
                        tick={{ fill: TEXT_SECONDARY, fontSize: 12, fontFamily: MONO_FONT }}
                    />
                    <YAxis
                        tick={{ fill: TEXT_SECONDARY, fontSize: 12, fontFamily: MONO_FONT }}
                        tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <RechartsTooltip
                        contentStyle={{
                            background: CARD_BG,
                            border: `1px solid ${GOLD_BORDER}`,
                            borderRadius: 4,
                            color: TEXT_PRIMARY,
                            fontFamily: MONO_FONT,
                            fontSize: 13,
                        }}
                        formatter={(value) => [`$${value.toLocaleString()}`, undefined]}
                    />
                    <Legend
                        wrapperStyle={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontSize: 12 }}
                    />
                    <Bar dataKey="Siloed" fill={TEXT_SECONDARY} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="Orchestrated" fill={GOLD} radius={[2, 2, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </Paper>
    );
}

function AgentBreakdown({ siloed, orchestrated }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER } = useThemeColors();
    const siloedAgents = siloed.agents || {};
    const orchAgents = orchestrated.agents || {};
    const allIds = [...new Set([...Object.keys(siloedAgents), ...Object.keys(orchAgents)])];

    if (allIds.length === 0) return null;

    const agentLabels = {
        battery: 'Battery',
        site_dna: 'Solar + Battery',
        ev_fleet: 'EV Fleet',
        cold_storage: 'Cold Storage',
    };

    return (
        <Paper sx={{ p: 3, mb: 4, background: CARD_BG, border: `1px solid ${GOLD_BORDER}` }}>
            <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT, mb: 2 }}>
                Per-Agent Breakdown
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['Agent', 'Siloed Energy', 'Siloed Demand', 'Orch Energy', 'Orch Demand', 'Savings'].map(h => (
                                <TableCell key={h} sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontSize: '0.75rem', borderBottom: `1px solid ${GOLD_BORDER}` }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allIds.map((aid) => {
                            const s = siloedAgents[aid] || {};
                            const o = orchAgents[aid] || {};
                            const siloedTotal = (s.energy_cost || 0) + (s.demand_cost || 0);
                            const orchTotal = (o.energy_cost || 0) + (o.demand_cost || 0);
                            const saved = siloedTotal - orchTotal;

                            return (
                                <TableRow key={aid} sx={{ '& td': { borderBottom: `1px solid ${GOLD_BORDER}` } }}>
                                    <TableCell sx={{ color: TEXT_PRIMARY }}>{agentLabels[aid] || aid}</TableCell>
                                    <TableCell sx={{ color: TEXT_PRIMARY, fontFamily: MONO_FONT }}>{fmtCost(s.energy_cost)}</TableCell>
                                    <TableCell sx={{ color: TEXT_PRIMARY, fontFamily: MONO_FONT }}>{fmtCost(s.demand_cost)}</TableCell>
                                    <TableCell sx={{ color: GOLD, fontFamily: MONO_FONT }}>{fmtCost(o.energy_cost)}</TableCell>
                                    <TableCell sx={{ color: GOLD, fontFamily: MONO_FONT }}>{fmtCost(o.demand_cost)}</TableCell>
                                    <TableCell sx={{ color: saved > 0 ? SUCCESS : TEXT_SECONDARY, fontFamily: MONO_FONT, fontWeight: 600 }}>
                                        {saved > 0 ? `-$${Math.round(saved).toLocaleString()}` : fmtCost(saved)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

function RunHistoryTable({ runs, activeRunId, onView, onDelete }) {
    const { CARD_BG, TEXT_PRIMARY, TEXT_SECONDARY, GOLD_BORDER, GOLD_HOVER } = useThemeColors();
    return (
        <Paper sx={{ background: CARD_BG, border: `1px solid ${GOLD_BORDER}`, overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: `2px solid ${GOLD}` }}>
                <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontFamily: HEADING_FONT }}>
                    Past Runs
                </Typography>
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {['Run', 'Status', 'Agents', 'Savings', 'Date', 'Actions'].map(h => (
                                <TableCell key={h} sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontSize: '0.75rem', borderBottom: `1px solid ${GOLD_BORDER}` }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {runs.map((run) => {
                            const isActive = run.id === activeRunId;
                            const st = run.status === 'completed'
                                ? { color: SUCCESS, icon: <CheckIcon fontSize="small" />, label: 'Done' }
                                : run.status === 'failed'
                                    ? { color: ERROR_COLOR, icon: <ErrorIcon fontSize="small" />, label: 'Failed' }
                                    : { color: GOLD, icon: <LoopIcon fontSize="small" />, label: run.status || 'Pending' };

                            const runSavings = run.results?.comparison?.savings?.total_cost;
                            const configAgents = run.config?.agents || [];

                            return (
                                <TableRow
                                    key={run.id}
                                    hover
                                    selected={isActive}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': { background: GOLD_HOVER },
                                        '&.Mui-selected': { background: 'rgba(178,140,80,0.08)' },
                                        '& td': { borderBottom: `1px solid ${GOLD_BORDER}` },
                                    }}
                                    onClick={() => onView(run)}
                                >
                                    <TableCell sx={{ color: TEXT_PRIMARY }}>
                                        {run.config?.name || `Run ${run.id?.slice(-6) || '???'}`}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={st.label}
                                            icon={st.icon}
                                            size="small"
                                            sx={{ color: st.color, borderColor: st.color, fontFamily: MONO_FONT, fontSize: '0.7rem' }}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: TEXT_SECONDARY, fontFamily: MONO_FONT, fontSize: '0.8rem' }}>
                                        {configAgents.join(', ') || '-'}
                                    </TableCell>
                                    <TableCell sx={{ color: runSavings > 0 ? GOLD : TEXT_SECONDARY, fontFamily: MONO_FONT, fontWeight: 600 }}>
                                        {runSavings != null ? `$${Math.round(runSavings).toLocaleString()}` : '-'}
                                    </TableCell>
                                    <TableCell sx={{ color: TEXT_SECONDARY, fontSize: '0.8rem' }}>
                                        {run.created_at ? new Date(run.created_at).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                            <Tooltip title="View results">
                                                <IconButton size="small" onClick={() => onView(run)} sx={{ color: GOLD }}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete run">
                                                <IconButton size="small" onClick={() => onDelete(run.id)} sx={{ color: ERROR_COLOR }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

// --- Helpers ---

function fmtCost(val) {
    if (val == null) return 'N/A';
    return `$${Math.round(val).toLocaleString()}`;
}

